import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, MessageCircle, Maximize2 } from "lucide-react";
import { Reveal, SectionHead, EASE } from "./Reveal";
import { PROTOTYPES, waLink, WA_MESSAGES } from "./data";

export default function Prototypes() {
  const [active, setActive] = useState("a");
  const p = PROTOTYPES[active];
  const waMsg = active === "a" ? WA_MESSAGES.prototipoA : WA_MESSAGES.prototipoB;

  return (
    <section id="departamentos" data-testid="prototypes-section" className="relative py-24 sm:py-32 lg:py-40 bg-[#18231c]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHead
            eyebrow="Departamentos"
            title={<>Dos prototipos.<br />Una forma de <span className="gold-text italic">vivir mejor</span></>}
          />
          <Reveal delay={0.15} className="shrink-0">
            <div className="inline-flex rounded-full border border-white/10 p-1 bg-[#213029]">
              {Object.values(PROTOTYPES).map((pr) => (
                <button
                  key={pr.id}
                  data-testid={`prototype-tab-${pr.id}`}
                  onClick={() => setActive(pr.id)}
                  className={`relative rounded-full px-6 py-3 text-sm font-medium transition-colors ${
                    active === pr.id ? "text-[#f4f2ea]" : "text-[#a8ac9b] hover:text-[#f4f2ea]"
                  }`}
                >
                  {active === pr.id && (
                    <motion.span
                      layoutId="proto-pill"
                      className="absolute inset-0 rounded-full bg-[#3f5a4a]"
                      transition={{ duration: 0.5, ease: EASE }}
                    />
                  )}
                  <span className="relative z-10">{pr.name}</span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-14 lg:mt-20 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.55, ease: EASE }}
              >
                <div className="flex items-end justify-between mb-8">
                  <div>
                    <p className="mono text-[11px] uppercase tracking-[0.3em] text-[#bfa360] mb-2">{p.units} unidades disponibles</p>
                    <h3 className="font-display text-4xl sm:text-5xl text-[#f4f2ea]">{p.name}</h3>
                  </div>
                  <p data-testid="prototype-size" className="font-display text-3xl sm:text-5xl text-[#ddc68c] whitespace-nowrap">{p.size}</p>
                </div>
                <p className="text-[#a8ac9b] leading-relaxed mb-4">{p.tagline}</p>
                <p data-testid="prototype-price" className="font-display text-3xl sm:text-4xl text-[#ddc68c] mb-8">
                  Desde {p.price} <span className="mono text-[10px] tracking-[0.2em] text-[#a8ac9b]">MXN</span>
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3.5 mb-10">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-[#ccd1c0]">
                      <Check size={16} className="mt-0.5 shrink-0 text-[#bfa360]" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-4">
                  <a
                    data-testid={`prototype-cta-${active}`}
                    href={waLink(waMsg)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-3 rounded-full bg-[#bfa360] px-7 py-4 text-sm font-semibold text-[#1c242f] hover:bg-[#ddc68c] transition-colors"
                  >
                    <MessageCircle size={16} />
                    Cotizar {p.name}
                  </a>
                  <p className="mono text-[10px] uppercase tracking-[0.2em] text-[#a8ac9b] self-center">
                    Informes directo<br />por WhatsApp
                  </p>
                </div>
                <p className="mt-5 mono text-[9px] uppercase tracking-[0.18em] text-[#8a8f82]">
                  *El costo se incrementa de acuerdo al nivel del departamento · 7 niveles + planta baja
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="order-1 lg:order-2">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 0.96, rotate: -1 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="img-frame relative rounded-2xl border border-white/10 bg-[#213029] p-6 sm:p-10"
                data-testid="prototype-plan-frame"
              >
                <img src={p.plan} alt={`Plano ${p.name}`} loading="lazy" decoding="async" className="w-full h-auto max-h-[520px] object-contain" />
                <div className="absolute top-4 right-4 mono text-[10px] uppercase tracking-[0.2em] text-[#a8ac9b] flex items-center gap-2">
                  <Maximize2 size={12} className="text-[#bfa360]" /> Plano ilustrativo
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
